import { C } from '../data/constants.js';

export class AudioManager {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.sfxGain = null;
    this.musicGain = null;
    
    // Persistent Audio Preferences
    this.musicEnabled = this._loadPref('bricksYoMusic', true);
    this.sfxEnabled = this._loadPref('bricksYoSFX', true);
    this.muted = this._loadPref('bricksYoMuted', false);
    
    this.initialized = false;

    // Music Sequencer state
    this.currentTrack = 'title'; // default to title theme
    this.isPlayingMusic = false;
    this.seqTimer = null;
    this.currentStep = 0;
    this.bpm = 116;
    this.trackPattern = null;
  }

  _loadPref(key, defaultVal) {
    try {
      const val = localStorage.getItem(key);
      return val !== null ? val === 'true' : defaultVal;
    } catch {
      return defaultVal;
    }
  }

  _savePref(key, val) {
    try {
      localStorage.setItem(key, String(val));
    } catch {}
  }

  init() {
    if (this.initialized) {
      if (this.ctx && this.ctx.state === 'suspended') {
        this.ctx.resume().catch(() => {});
      }
      if (this.musicEnabled && !this.isPlayingMusic && this.currentTrack) {
        this.playTrack(this.currentTrack);
      }
      return;
    }
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    this.ctx = new AudioCtx();

    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.muted ? 0 : 0.55;
    this.masterGain.connect(this.ctx.destination);

    // SFX Gain channel
    this.sfxGain = this.ctx.createGain();
    this.sfxGain.gain.value = this.sfxEnabled ? 0.85 : 0;
    this.sfxGain.connect(this.masterGain);

    // Music Gain channel (warm, clear synthesizer volume)
    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.value = this.musicEnabled ? 0.5 : 0;
    this.musicGain.connect(this.masterGain);

    this.initialized = true;

    if (this.currentTrack && this.musicEnabled) {
      this.playTrack(this.currentTrack);
    }
  }

  toggleMusic() {
    this.init();
    this.musicEnabled = !this.musicEnabled;
    this._savePref('bricksYoMusic', this.musicEnabled);

    if (this.musicGain && this.ctx) {
      this.musicGain.gain.setValueAtTime(this.musicEnabled ? 0.5 : 0, this.ctx.currentTime);
    }

    if (this.musicEnabled) {
      if (this.currentTrack && this.currentTrack !== 'none') {
        this.playTrack(this.currentTrack);
      }
    } else {
      this.stopMusic();
    }
    return this.musicEnabled;
  }

  toggleSFX() {
    this.init();
    this.sfxEnabled = !this.sfxEnabled;
    this._savePref('bricksYoSFX', this.sfxEnabled);

    if (this.sfxGain && this.ctx) {
      this.sfxGain.gain.setValueAtTime(this.sfxEnabled ? 0.85 : 0, this.ctx.currentTime);
    }
    return this.sfxEnabled;
  }

  toggleMute() {
    this.init();
    this.muted = !this.muted;
    this._savePref('bricksYoMuted', this.muted);

    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setValueAtTime(this.muted ? 0 : 0.55, this.ctx.currentTime);
    }
    return this.muted;
  }

  // --- Helper: play a tone ---
  _tone(freq, duration, type = 'square', gainVal = 0.15) {
    if (!this.initialized || this.muted || !this.sfxEnabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, this.ctx.currentTime);
    gain.gain.setValueAtTime(gainVal, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(this.sfxGain || this.masterGain);
    osc.start();
    osc.stop(this.ctx.currentTime + duration);
  }

  // --- Helper: play noise burst (for impacts) ---
  _noise(duration, gainVal = 0.1) {
    if (!this.initialized || this.muted || !this.sfxEnabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    const bufferSize = Math.floor(this.ctx.sampleRate * duration);
    if (bufferSize <= 0) return;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    }
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(gainVal, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + duration);
    source.connect(gain);
    gain.connect(this.sfxGain || this.masterGain);
    source.start();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 🎵 Procedural Synth Background Music Engine
  // ─────────────────────────────────────────────────────────────────────────────

  playTrack(trackName) {
    if (this.currentTrack === trackName && this.isPlayingMusic) return;
    this.currentTrack = trackName;
    this.currentStep = 0;

    if (!this.initialized) return;

    if (!trackName || trackName === 'none' || !this.musicEnabled) {
      this.stopMusic();
      return;
    }

    this._setupTrackPatterns(trackName);
    this._startSequencer();
  }

  stopMusic() {
    this.isPlayingMusic = false;
    if (this.seqTimer) {
      clearInterval(this.seqTimer);
      this.seqTimer = null;
    }
  }

  _setupTrackPatterns(trackName) {
    // Note frequencies in Hz
    const N = {
      C2: 65.41, Db2: 69.30, D2: 73.42, Eb2: 77.78, F2: 87.31, Gb2: 92.50, G2: 98.00, Ab2: 103.83, Bb2: 116.54,
      C3: 130.81, D3: 146.83, Eb3: 155.56, F3: 174.61, G3: 196.00, Ab3: 207.65, Bb3: 233.08,
      C4: 261.63, Db4: 277.18, D4: 293.66, Eb4: 311.13, F4: 349.23, G4: 392.00, Ab4: 415.30, Bb4: 466.16,
      C5: 523.25, D5: 587.33, Eb5: 622.25, Gb5: 739.99, G5: 783.99,
      _: 0
    };

    switch (trackName) {
      case 'sector1': // Neon Grid (122 BPM Synthwave)
        this.bpm = 122;
        this.trackPattern = {
          bass: [N.C2, N._, N.C2, N._, N.Eb2, N._, N.F2, N._, N.G2, N._, N.G2, N._, N.Bb2, N._, N.G2, N._],
          lead: [N.C4, N.Eb4, N.G4, N.C5, N.G4, N.Eb4, N.F4, N.Ab4, N.G4, N.Eb4, N.D4, N.Eb4, N.C4, N.G4, N.Eb4, N.D4],
          kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
          leadType: 'sine',
          bassType: 'sawtooth'
        };
        break;

      case 'sector2': // Cyber Depths (104 BPM Sub-Aquatic Ambient Pulse)
        this.bpm = 104;
        this.trackPattern = {
          bass: [N.F2, N._, N._, N.F2, N._, N._, N.Ab2, N._, N.Eb2, N._, N._, N.Eb2, N._, N._, N.G2, N._],
          lead: [N.C4, N._, N.Eb4, N._, N.G4, N._, N.Bb4, N._, N.Ab4, N._, N.G4, N._, N.Eb4, N._, N.F4, N._],
          kick: [1, 0, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 1, 0],
          leadType: 'triangle',
          bassType: 'sine'
        };
        break;

      case 'sector3': // Solar Forge (134 BPM Driving Industrial)
        this.bpm = 134;
        this.trackPattern = {
          bass: [N.D2, N.D2, N.F2, N.D2, N.G2, N.D2, N.Ab2, N.G2, N.D2, N.D2, N.F2, N.D2, N.C2, N.D2, N.Eb2, N.F2],
          lead: [N.D4, N.F4, N.A4, N.D5, N.C5, N.A4, N.F4, N.G4, N.F4, N.D4, N.F4, N.A4, N.C5, N.D5, N.A4, N.F4],
          kick: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1],
          leadType: 'square',
          bassType: 'sawtooth'
        };
        break;

      case 'sector4': // Void Nexus (140 BPM Dark Cosmic Phrygian)
        this.bpm = 140;
        this.trackPattern = {
          bass: [N.C2, N.Eb2, N.C2, N.Db2, N.C2, N.G2, N.C2, N.Eb2, N.C2, N.Db2, N.C2, N.Bb2, N.Ab2, N.G2, N.F2, N.Db2],
          lead: [N.C4, N.Db4, N.C4, N.G4, N.Eb4, N.Db4, N.C4, N.Bb3, N.C4, N.Eb4, N.G4, N.Ab4, N.G4, N.Eb4, N.Db4, N.C4],
          kick: [1, 0, 0, 1, 1, 0, 0, 1, 1, 0, 0, 1, 1, 0, 1, 0],
          leadType: 'sawtooth',
          bassType: 'square'
        };
        break;

      case 'boss': // Boss Battle (146 BPM High-Octane Alarm Ostinato)
        this.bpm = 146;
        this.trackPattern = {
          bass: [N.C2, N.C2, N.C3, N.C2, N.Eb2, N.Eb2, N.Eb3, N.Eb2, N.F2, N.F2, N.F3, N.F2, N.Gb2, N.Gb2, N.G2, N.Ab2],
          lead: [N.C5, N.Eb5, N.C5, N.Gb5, N.F5, N.Eb5, N.C5, N.Bb4, N.C5, N.Eb5, N.Gb5, N.G5, N.Gb5, N.Eb5, N.C5, N.Eb5],
          kick: [1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 1, 1],
          leadType: 'square',
          bassType: 'sawtooth'
        };
        break;

      case 'title':
      default:
        this.bpm = 116;
        this.trackPattern = {
          bass: [N.C2, N._, N._, N.C2, N.G2, N._, N._, N.G2, N.Eb2, N._, N._, N.Eb2, N.Bb2, N._, N.G2, N._],
          lead: [N.C4, N.G4, N.Eb4, N.C4, N.D4, N.G4, N.Eb4, N.D4, N.Eb4, N.Bb4, N.G4, N.Eb4, N.F4, N.C5, N.Ab4, N.G4],
          kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
          leadType: 'sine',
          bassType: 'triangle'
        };
        break;
    }
  }

  _startSequencer() {
    this.stopMusic();
    if (!this.trackPattern || !this.musicEnabled) return;

    this.isPlayingMusic = true;
    const stepDurationMs = (60 / this.bpm / 4) * 1000; // 16th note steps

    this.seqTimer = setInterval(() => {
      if (!this.isPlayingMusic || this.muted || !this.musicEnabled || !this.ctx) return;
      this._playStep(this.currentStep);
      this.currentStep = (this.currentStep + 1) % 16;
    }, stepDurationMs);
  }

  _playStep(step) {
    if (!this.trackPattern || !this.ctx || this.muted || !this.musicEnabled) return;
    const pat = this.trackPattern;

    // Bass note
    const bassFreq = pat.bass[step];
    if (bassFreq > 0) {
      this._synthVoice(bassFreq, 0.18, pat.bassType, 0.24, this.musicGain);
    }

    // Lead note
    const leadFreq = pat.lead[step];
    if (leadFreq > 0) {
      this._synthVoice(leadFreq, 0.14, pat.leadType, 0.18, this.musicGain);
    }

    // Kick / Percussion
    if (pat.kick[step]) {
      this._synthKick(0.10, 0.28);
    }
  }

  _synthVoice(freq, duration, type, gainVal, destGain) {
    if (!this.ctx) return;
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, this.ctx.currentTime);
    gain.gain.setValueAtTime(gainVal, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(destGain || this.masterGain);
    osc.start();
    osc.stop(this.ctx.currentTime + duration);
  }

  _synthKick(duration, gainVal) {
    if (!this.ctx) return;
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(140, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(35, this.ctx.currentTime + duration);
    gain.gain.setValueAtTime(gainVal, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(this.musicGain || this.masterGain);
    osc.start();
    osc.stop(this.ctx.currentTime + duration);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 💥 SFX Methods
  // ─────────────────────────────────────────────────────────────────────────────

  paddleHit(hitPos = 0) {
    const baseFreq = 440 + Math.abs(hitPos) * 220;
    this._tone(baseFreq, 0.09, 'sine', 0.14);
  }

  wallHit() {
    this._tone(220, 0.04, 'sine', 0.06);
  }

  brickDestroy(comboCount = 0) {
    const semitone = Math.min(comboCount, 16);
    const freq = 261.63 * Math.pow(2, semitone / 12);
    this._tone(freq, 0.14, 'square', 0.11);
    this._noise(0.06, 0.08);
  }

  armoredHit() {
    this._tone(150, 0.12, 'sawtooth', 0.09);
    this._tone(180, 0.08, 'triangle', 0.07);
  }

  tntExplode() {
    this._tone(65, 0.45, 'sine', 0.22);
    this._tone(45, 0.55, 'sawtooth', 0.12);
    this._noise(0.35, 0.18);
  }

  chainLink(chainIndex) {
    const intervals = [0, 4, 7, 12, 16, 19, 24];
    const semitone = intervals[chainIndex % intervals.length];
    const freq = 330 * Math.pow(2, semitone / 12);
    this._tone(freq, 0.12, 'sine', 0.12);
  }

  buttonClick() {
    this._tone(880, 0.04, 'sine', 0.10);
    setTimeout(() => this._tone(1320, 0.05, 'sine', 0.12), 25);
  }

  powerUpDrop() {
    this._tone(587.33, 0.08, 'sine', 0.09);
  }

  powerUpCollect() {
    this._tone(523.25, 0.08, 'sine', 0.12);
    setTimeout(() => this._tone(659.25, 0.08, 'sine', 0.12), 60);
    setTimeout(() => this._tone(783.99, 0.12, 'sine', 0.14), 120);
  }

  powerUpCatch() {
    this.powerUpCollect();
  }

  laserShot() {
    this._tone(880, 0.06, 'sawtooth', 0.08);
  }

  laserHit() {
    this._tone(350, 0.05, 'square', 0.07);
    this._noise(0.03, 0.05);
  }

  ballMiss() {
    this._tone(200, 0.35, 'sawtooth', 0.15);
  }

  levelClear() {
    const notes = [523.25, 659.25, 783.99, 1046.50];
    notes.forEach((freq, i) => {
      setTimeout(() => this._tone(freq, 0.18, 'sine', 0.15), i * 100);
    });
  }

  bossHit() {
    this._tone(120, 0.15, 'sawtooth', 0.12);
    this._noise(0.08, 0.10);
  }

  bossDefeated() {
    const notes = [261.63, 329.63, 392.00, 523.25, 659.25, 783.99];
    notes.forEach((freq, i) => {
      setTimeout(() => this._tone(freq, 0.25, 'triangle', 0.18), i * 120);
    });
  }

  prismSplit() {
    this._tone(600, 0.06, 'sine', 0.09);
    setTimeout(() => this._tone(900, 0.08, 'sine', 0.11), 40);
  }

  empBlast() {
    this._tone(300, 0.25, 'sawtooth', 0.16);
    this._noise(0.20, 0.12);
  }

  gameOver() {
    const notes = [440, 392, 349.23, 293.66, 261.63, 220, 164.81];
    notes.forEach((freq, i) => {
      setTimeout(() => this._tone(freq, 0.28, 'sawtooth', 0.16), i * 130);
    });
  }
}
